/**
 * 画面一覧と画面詳細で、入った直後には見せない補足を「指すと出す文字」（title）へ畳む関数。
 *
 * 札や小さい行として並べていた補足（未翻訳・URL パターン・種類・件数）を title に回すと、入った
 * 直後に見える物が名前だけになる（Dhik 2026-09-29「本当に出すべき情報を 1 or 2 個にするぐらい
 * 情報を絞ってほしい」）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面一覧と画面詳細の補足（未翻訳・URL パターン・種類・件数）を、指すと出す文字に畳む。
 * @graph-connects none
 */
import type { LabelTranslate } from '../components/spec/UntranslatedBadge.js';

/**
 * 未翻訳の印の文字。名前が空の行は本文（`displayLabel`）が「未設定」「未翻訳」と言うので出さない
 * （`SpecLabelBadges` と同じ線）。
 *
 * @param label 選んだ言語へ畳んだ表示名
 * @param untranslated 選んだ言語に訳が入っていないか
 * @param translate 表示言語への翻訳
 * @returns 未翻訳の印の文字。出さないなら null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 名前のある表示名に訳が欠けているときだけ、未翻訳の印の文字を返す。
 * @graph-connects none
 */
export function untranslatedHint(
  label: string | null,
  untranslated: boolean,
  translate: LabelTranslate,
): string | null {
  return (label ?? '') !== '' && untranslated ? translate('spec.untranslated') : null;
}

/**
 * 補足の行を改行で繋いで、指すと出す文字にする。行が 1 つも無ければ title を付けない。
 *
 * @param lines 補足の行（出さない行は null）
 * @returns 改行で繋いだ文字。行が無ければ undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 補足の行を改行で繋ぎ、行が無ければ指すと出す文字を付けない。
 * @graph-connects none
 */
export function joinHint(lines: readonly (string | null)[]): string | undefined {
  const present = lines.filter((line): line is string => line !== null && line !== '');
  return present.length === 0 ? undefined : present.join('\n');
}
